import { useState } from 'react'
import { InlineFieldGroup, type InlineGroupField } from '@/components/inline-field-group'
import { SupplierRecipientEnvelopeEditor } from '@/components/supplier-recipient-envelope-editor'
import type { EmailRecipients, EmailRecipientsState } from '@/components/use-email-recipients'

type Key = 'to' | 'cc'

const FIELDS: ReadonlyArray<InlineGroupField<Key>> = [
  { key: 'to', label: 'To', required: true, autoComplete: 'email', inputMode: 'email' },
  { key: 'cc', label: 'CC', autoComplete: 'email', inputMode: 'email' },
]

function addresses(value: string): string[] {
  const seen = new Set<string>()
  return value
    .split(/[,;\s]+/)
    .map((address) => address.trim())
    .filter((address) => {
      const key = address.toLowerCase()
      if (!address || seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const invalidAddresses = (value: string) =>
  addresses(value).filter((address) => !SupplierRecipientEnvelopeEditor.isValidEmail(address))


/**
 * The To and CC lines above an action item's email. Clicking them edits both in place; the change applies
 * to this email only. `toName` names the saved recipient while it is still the one emailed.
 */
export function EmailRecipientsField({
  state,
  toName,
  fallback = 'Loading…',
  disabled = false,
  onChange,
}: {
  state: EmailRecipientsState
  toName?: string | null
  /** Shown as To while who it goes to is not known yet. */
  fallback?: string
  disabled?: boolean
  /** Saves the change at once, for a draft that keeps its own recipients. */
  onChange?: (next: EmailRecipients) => void
}) {
  const [editing, setEditing] = useState(false)
  const recipients = state.recipients
  const to = recipients?.to ?? []
  const cc = recipients?.cc ?? []
  const named = Boolean(toName) && !state.changed && to.length === 1

  return (
    <div className="py-1">
    <InlineFieldGroup
      label="email recipients"
      fields={FIELDS}
      values={{ to: to.join(', '), cc: cc.join(', ') }}
      editing={editing}
      onEditingChange={setEditing}
      disabled={disabled || recipients === undefined}
      saveLabel="Use these recipients"
      labelled
      invalid={(draft) => [
        ...(invalidAddresses(draft.to).length ? (['to'] as const) : []),
        ...(invalidAddresses(draft.cc).length ? (['cc'] as const) : []),
      ]}
      notice={(draft) => {
        const bad = [...invalidAddresses(draft.to), ...invalidAddresses(draft.cc)]
        return bad.length
          ? `Not an email address: ${bad.join(', ')}`
          : 'Separate several addresses with commas. This email only.'
      }}
      onSave={(draft) => {
        const nextTo = addresses(draft.to)
        const toKeys = new Set(nextTo.map((address) => address.toLowerCase()))
        const next = { to: nextTo, cc: addresses(draft.cc).filter((address) => !toKeys.has(address.toLowerCase())) }
        state.change(next)
        onChange?.(next)
        setEditing(false)
      }}
    >
      <span className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm">
        <span className="font-medium">To</span>
        <span className="min-w-0 break-words text-foreground/80">
          {to.length === 0 ? (
            <span className="text-muted-foreground">{fallback}</span>
          ) : named ? (
            <>
              {toName} <span className="text-muted-foreground">{to[0]}</span>
            </>
          ) : (
            to.join(', ')
          )}
        </span>
        <span className="font-medium">CC</span>
        <span className="min-w-0 break-words text-foreground/80">
          {cc.length ? cc.join(', ') : <span className="text-muted-foreground">Add CC</span>}
        </span>
      </span>
    </InlineFieldGroup>
    </div>
  )
}
